import { motion } from "framer-motion";
import { CalendarDays, FileDown, GraduationCap, Magnet, Play, RefreshCcw } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6 },
};

const IconChip = ({ Icon }) => (
  <span className="w-9 h-9 rounded-xl bg-[#FDF8EA] border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
    <Icon size={16} className="text-[#AA771C]" strokeWidth={1.8} />
  </span>
);

const AVATARS = ["JB", "KL", "AR", "TS"];

export const MzProducts = () => (
  <section className="py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div {...fadeUp} className="max-w-2xl mb-12 sm:mb-16">
        <p className="mz-eyebrow mb-3">Sell everything from one link</p>
        <h2 className="mz-h text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
          Five ways to <span className="mz-gold-text">get paid.</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
        {/* Digital Downloads - large */}
        <motion.article {...fadeUp} className="mz-card md:col-span-4 p-6 sm:p-8 flex flex-col sm:flex-row gap-6 sm:items-center" data-testid="product-card-downloads">
          <div className="flex-1">
            <IconChip Icon={FileDown} />
            <h3 className="mz-h text-lg font-semibold mt-4">Digital Downloads</h3>
            <p className="mt-1.5 text-sm text-neutral-500 leading-relaxed">
              Ebooks, templates and guides. Delivered the second checkout clears.
            </p>
          </div>
          <div className="flex-1 rounded-2xl bg-[#FAFAFA] border border-neutral-100 p-5">
            <div className="flex items-center gap-3">
              <span className="mz-gold-fill w-10 h-10 rounded-xl flex items-center justify-center">
                <FileDown size={16} className="text-[#1a1206]" strokeWidth={2} />
              </span>
              <div>
                <p className="text-[12px] font-semibold text-neutral-800">Glow-Up-Guide.pdf</p>
                <p className="text-[11px] text-neutral-400">$29 · 214 sold</p>
              </div>
            </div>
            <div className="mt-4 h-1.5 rounded-full bg-neutral-200 overflow-hidden">
              <motion.div
                className="mz-gold-fill h-full rounded-full"
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, delay: 0.3 }}
              />
            </div>
            <p className="mt-2.5 text-[10.5px] font-semibold text-[#AA771C]">Delivered instantly</p>
          </div>
        </motion.article>

        {/* Courses */}
        <motion.article {...fadeUp} className="mz-card md:col-span-2 p-6" data-testid="product-card-courses">
          <IconChip Icon={GraduationCap} />
          <h3 className="mz-h text-lg font-semibold mt-4">Online Courses</h3>
          <p className="mt-1.5 text-sm text-neutral-500">Multi-module video or text. Access is forever.</p>
          <div className="mt-5 rounded-xl bg-[#0A0A0A] aspect-video relative overflow-hidden">
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="mz-gold-fill w-10 h-10 rounded-full flex items-center justify-center shadow-[0_8px_24px_rgba(212,175,55,0.5)]">
                <Play size={14} className="text-[#1a1206] ml-0.5" strokeWidth={2.4} fill="#1a1206" />
              </span>
            </span>
            <div className="absolute bottom-3 inset-x-3">
              <div className="h-1 rounded-full bg-white/15 overflow-hidden">
                <div className="mz-gold-fill h-full w-[62%] rounded-full" />
              </div>
              <p className="mt-1.5 text-[9.5px] text-white/50 font-medium">Module 3 of 8 · 62%</p>
            </div>
          </div>
        </motion.article>

        {/* Memberships */}
        <motion.article {...fadeUp} className="mz-card md:col-span-2 p-6" data-testid="product-card-memberships">
          <IconChip Icon={RefreshCcw} />
          <h3 className="mz-h text-lg font-semibold mt-4">Memberships</h3>
          <p className="mt-1.5 text-sm text-neutral-500">Recurring access to your community or coaching.</p>
          <div className="mt-5 flex items-center justify-between rounded-xl bg-[#FAFAFA] border border-neutral-100 px-4 py-3.5">
            <div className="flex items-center">
              {AVATARS.map((a, i) => (
                <span
                  key={a}
                  className="mz-gold-fill mz-h w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-[9px] font-bold text-[#1a1206]"
                  style={{ marginLeft: i === 0 ? 0 : -8, opacity: 1 - i * 0.12 }}
                >
                  {a}
                </span>
              ))}
              <span className="ml-2 text-[11px] font-semibold text-neutral-600">486 members</span>
            </div>
            <span className="text-[10px] font-bold text-[#AA771C] bg-[#FDF8EA] border border-[#D4AF37]/30 rounded-full px-2.5 py-1">$19/mo</span>
          </div>
        </motion.article>

        {/* Bookings */}
        <motion.article {...fadeUp} className="mz-card md:col-span-2 p-6" data-testid="product-card-bookings">
          <IconChip Icon={CalendarDays} />
          <h3 className="mz-h text-lg font-semibold mt-4">Bookings</h3>
          <p className="mt-1.5 text-sm text-neutral-500">Paid 1:1 calls and consults on your calendar.</p>
          <div className="mt-5 rounded-xl bg-[#FAFAFA] border border-neutral-100 p-4">
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: 14 }, (_, i) => (
                <span
                  key={i}
                  className={`h-6 rounded-md text-[9px] font-semibold flex items-center justify-center ${
                    i === 11 ? "mz-gold-fill text-[#1a1206]" : "bg-white border border-neutral-100 text-neutral-300"
                  }`}
                >
                  {i + 9}
                </span>
              ))}
            </div>
            <p className="mt-3 text-[10.5px] font-semibold text-[#AA771C]">Fri · 2:00 PM booked · $149</p>
          </div>
        </motion.article>

        {/* Lead Magnets */}
        <motion.article {...fadeUp} className="mz-card md:col-span-2 p-6" data-testid="product-card-leadmagnets">
          <IconChip Icon={Magnet} />
          <h3 className="mz-h text-lg font-semibold mt-4">Lead Magnets</h3>
          <p className="mt-1.5 text-sm text-neutral-500">Free opt-ins that feed your Growth Engine.</p>
          <div className="mt-5 rounded-xl bg-[#FAFAFA] border border-neutral-100 p-4">
            <div className="flex gap-2">
              <span className="flex-1 rounded-lg bg-white border border-neutral-200 px-3 py-2 text-[11px] text-neutral-300">you@email.com</span>
              <span className="mz-gold-fill mz-h rounded-lg px-3 py-2 text-[10.5px] font-bold text-[#1a1206]">Get it free</span>
            </div>
            <p className="mt-3 text-[10.5px] font-semibold text-[#AA771C]">+1,204 subscribers this month</p>
          </div>
        </motion.article>
      </div>
    </div>
  </section>
);
